<template>
  <div
    v-if="totalPages > 1"
    class="pagination"
  >
    <p
      class="pagination-label"
      v-html="label"
    />
    <nav class="pagination-links">
      <a
        class="pagination-links-item"
        :class="{ 'is-disabled pointer-events-none': disabled || currentPage == 1 }"
        @click.prevent="currentPage--"
      >
        <Icon icon="chevron-left" />
        <span class="sr-only">{{ $t("previous") }}</span>
      </a>

      <template v-for="page in pages">
        <a
          v-if="page"
          :key="page"
          class="pagination-links-item"
          :class="{ 'is-active': page == currentPage, 'is-disabled pointer-events-none': disabled }"
          @click.prevent="currentPage = page"
        >{{ page }}</a>
        <span
          v-else
          class="pagination-links-item is-disabled"
        >&hellip;</span>
      </template>

      <a
        class="pagination-links-item"
        :class="{ 'is-disabled pointer-events-none': disabled || currentPage == totalPages }"
        @click.prevent="currentPage++"
      >
        <span class="sr-only">{{ $t("next") }}</span>
        <Icon icon="chevron-right" />
      </a>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, computed } from 'vue'

const props = defineProps<{
  perPage: number
  totalItems: number
  totalPages: number
  disabled?: boolean
}>()

const currentPage = defineModel<number>({ default: 1 })

const { t } = useI18n()

const pages = computed(() => {
  const range = (start: number, end: number) => {
    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  }

  if (props.totalPages <= 7) {
    return range(1, props.totalPages)
  }

  if (currentPage.value <= 4) {
    return [...range(1, 5), null, props.totalPages]
  }

  if (currentPage.value >= props.totalPages - 3) {
    return [1, null, ...range(props.totalPages - 4, props.totalPages)]
  }

  return [1, null, ...range(currentPage.value - 1, currentPage.value + 1), null, props.totalPages]
})

const label = computed(() => {
  let label
  let itemsFrom
  let itemsTo
  const itemsTotal = props.totalItems

  if (props.totalItems == 1) {
    label = t('pagination_one_item')
  } else if (props.totalPages == 1) {
    label = t('pagination_items').replace(':itemsTotal', itemsTotal)
  } else {
    itemsFrom = (currentPage.value - 1) * props.perPage + 1
    itemsTo = Math.min(currentPage.value * props.perPage, props.totalItems)
    label = t('pagination')
  }

  return label.replace(':itemsFrom', itemsFrom).replace(':itemsTo', itemsTo).replace(':itemsTotal', itemsTotal)
})

const arrowKeyListener = (event) => {
  if (event.key === 'ArrowLeft' && currentPage.value > 1) {
    currentPage.value--
  } else if (event.key === 'ArrowRight' && currentPage.value < props.totalPages) {
    currentPage.value++
  }
}

onMounted(() => {
  document.addEventListener('keydown', arrowKeyListener)
})

onUnmounted(() => {
  document.removeEventListener('keydown', arrowKeyListener)
})
</script>

<style>
.pagination {
  @apply text-center md:flex md:items-center md:justify-between;

  &-links {
    @apply mx-auto mt-6 inline-flex flex-wrap md:-mr-3 md:mt-0 md:flex;

    &-item {
      @apply block rounded px-3 py-1 text-supplement transition-all;

      &.is-active {
        @apply bg-primary text-white transition-none;
      }

      &.is-disabled {
        @apply cursor-not-allowed opacity-50;

        &:hover {
          @apply text-supplement;
        }
      }
    }
  }
}
</style>
